import React, { useEffect, useState } from 'react';
import { Button, Card, Col, Container, DropdownItem, DropdownMenu, DropdownToggle, Input, Progress, Row, UncontrolledDropdown } from 'reactstrap';
import BreadcrumbTrail from '../../../../Common/BreadcrumbTrail';
import { Loader, PreviewCardHeader } from '../../../../Common';
import { useHistory } from 'react-router-dom';
import SpinTheWheel from './Components/From/SpinWheel';
import Quiz from './Components/From/Quiz';
import queryString from 'query-string';
import { Constant } from '../../../../Helpers/constant';
import { useDispatch, useSelector } from 'react-redux';
import { GET_GAMIFICATION_LIST, GET_GAMIFICATION_LIST_EMPTY, UPDATE_GAME_STATUS, UPDATE_GAME_STATUS_EMPTY } from '../../../../../store/actions';
import Table from './Components/Table/Table';
import UpdateModal from './Components/Modal/UpdateModal';
import CustomCard from '../Dashboard/Components/Card';
import totalGames from '../../../../../assets/images/total-games.png';
import liveGames from '../../../../../assets/images/live-games.png';
import expiredGames from '../../../../../assets/images/expired-games.png';

const Gamification = () => {
  const history = useHistory();
  const dispatch = useDispatch();

  // Parse URL query parameters
  const { page = 1, perPageLimit = Constant.TABLE_PAGE_SIZE } = queryString.parse(location.search);

  const [loading, setLoading] = useState(false);
  const [isOpenSpinTheWheel, setIsOpenSpinTheWheel] = useState(false);
  const [isOpenQuiz, setIsOpenQuiz] = useState(false);
  const [currentPageNumber, setCurrentPageNumber] = useState(Number(page) - 1); // `page` is 1-based
  const [rowsPerPage, setRowsPerPage] = useState(Number(perPageLimit));
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [currentSelectedGameDetails, setCurrentSelectedGameDetails] = useState(null);
  const [cardData, setCardData] = useState([]);
  const [updateGameState, setUpdateGameState] = useState('');

  /* ---------------------------- REDUX STATES ---------------------------- */
  // Get Gamification List
  const {
    getGamificationList,
    getGamificationListLoading,
    getBenefitsLoading,
    createUploadTicketLoading,
    createSpin,
    createSpinLoading,
    createQuiz,
    createQuizLoading,
    updateGameStatus,
  } = useSelector((state: any) => ({
    getGamificationList: state?.GetGamificationList?.data,
    getGamificationListLoading: state?.GetGamificationList?.loading,

    getBenefitsLoading: state?.GetBenefitsGamification?.loading,
    createUploadTicketLoading: state?.PostImage?.loading,

    createSpin: state?.CreateSpinGame?.data,
    createSpinLoading: state?.CreateSpinGame?.loading,

    createQuiz: state?.CreateQuizGame?.data,
    createQuizLoading: state?.CreateQuizGame?.loading,

    updateGameStatus: state?.UpdateGameStatus?.data,
  }));

  useEffect(() => {
    if (getGamificationList !== null) {
      const count = getGamificationList?.total_games || 0;
      const live = getGamificationList?.live_games || 0;
      const expired = getGamificationList?.expired_games || 0;

      setCardData([
        { name: 'Total Games', count: count, icon: totalGames },
        { name: 'Live Games', count: live, icon: liveGames },
        { name: 'Games Expired', count: expired, icon: expiredGames },
      ]);
    }
  }, [getGamificationList]);

  useEffect(() => {
    const { page, perPageLimit } = queryString.parse(location.search);
    setCurrentPageNumber(Number(page || 1) - 1); // Default to 1-based page
    setRowsPerPage(Number(perPageLimit || Constant.TABLE_PAGE_SIZE));
  }, [location.search]);

  useEffect(() => {
    if (createSpin !== null || createQuiz !== null || updateGameStatus !== null) {
      setIsOpenSpinTheWheel(false);
      setIsOpenQuiz(false);
      setCurrentSelectedGameDetails(null);
      fetchGameList(0, Constant.TABLE_PAGE_SIZE);
      updateURLParams({ page: 1, perPageLimit: Constant.TABLE_PAGE_SIZE });
      dispatch({
        type: UPDATE_GAME_STATUS_EMPTY,
      });
    }
  }, [createSpin, createQuiz, updateGameStatus]);

  useEffect(() => {
    if (!isOpenQuiz && !isOpenSpinTheWheel) {
      fetchGameList();
    }
  }, [currentPageNumber, rowsPerPage, isOpenQuiz, isOpenSpinTheWheel]);

  useEffect(() => {
    if (getGamificationListLoading || getBenefitsLoading || createUploadTicketLoading || createQuizLoading || createSpinLoading) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [getGamificationListLoading, getBenefitsLoading, createUploadTicketLoading, createQuizLoading, createSpinLoading]);

  const fetchGameList = (customPage = currentPageNumber, customLimit = rowsPerPage) => {
    dispatch({
      type: GET_GAMIFICATION_LIST,
      payload: {
        urlParams: {
          offset: customPage * customLimit,
          limit: customLimit,
        },
      },
    });
  };

  const updateURLParams = newParams => {
    const currentParams = queryString.parse(location.search);
    const updatedParams = {
      page: currentPageNumber + 1, // Default to current page
      perPageLimit: rowsPerPage, // Default to current rowsPerPage
      ...currentParams, // Merge existing query parameters
      ...newParams, // Override with new parameters
    };

    const newUrl = queryString.stringify(updatedParams);

    history.push({
      pathname: location.pathname,
      search: `?${newUrl}`,
    });
  };

  /* ---------------------------- PAGINATION HANDLER ---------------------------- */
  const paginationHandler = pageNumber => {
    updateURLParams({ page: pageNumber + 1 });
  };

  const handleRowsPerPageChange = newRowsPerPage => {
    updateURLParams({ page: 1, perPageLimit: newRowsPerPage });
  };

  /* ---------------------------- BREADCRUMB HANDLER ---------------------------- */
  const breadcrumNavigationHandler = (title: string) => {
    if (title === `Dashboard`) {
      history.push(`/client/dashboard`);
    }
  };

  /* ---------------------------- TABLE ACTION HANDLER ---------------------------- */
  const tableActionHandler = params => {
    setCurrentSelectedGameDetails(params?.gameDetails);
    if (params?.actionType === `EXPIRE`) {
      setIsUpdateModalOpen(true);
      setUpdateGameState('expired');
    } else if (params?.actionType === `LIVE`) {
      setIsUpdateModalOpen(true);
      setUpdateGameState('live');
    } else if (params?.actionType === `UPDATE`) {
      if (params?.gameDetails?.game_type === 'quiz') {
        setIsOpenQuiz(true);
      } else if (params?.gameDetails?.game_type === 'spin') {
        setIsOpenSpinTheWheel(true);
      }
      dispatch({
        type: GET_GAMIFICATION_LIST_EMPTY,
      });
    }
  };

  return (
    <>
      {loading && <Loader />}

      {isUpdateModalOpen && (
        <UpdateModal
          onClose={() => {
            setIsUpdateModalOpen(false);
            setUpdateGameState('');
          }}
          currentGameDetails={currentSelectedGameDetails}
          updateType={updateGameState}
        />
      )}

      <div className="page-content">
        <Container fluid className="position-relative">
          <div id="main-voucher-container">
            <BreadcrumbTrail title="Gamification" navigationby={breadcrumNavigationHandler} navigation={[`Dashboard`, `Gamification`]} />
            {!isOpenQuiz && !isOpenSpinTheWheel && (
              <div>
                <UncontrolledDropdown className="input-group">
                  <DropdownToggle
                    tag="button"
                    className="btn btn-success fs-14 dropdown-toggle"
                    type="button"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >
                    Gamification
                  </DropdownToggle>
                  <DropdownMenu className="dropdown-menu-end ">
                    <li
                      className="fw-light"
                      onClick={() => {
                        dispatch({
                          type: GET_GAMIFICATION_LIST_EMPTY,
                        });
                        setIsOpenSpinTheWheel(!isOpenSpinTheWheel);
                      }}
                    >
                      <DropdownItem>Spin the Wheel</DropdownItem>
                    </li>
                    <li
                      className="fw-light"
                      onClick={() => {
                        dispatch({
                          type: GET_GAMIFICATION_LIST_EMPTY,
                        });
                        setIsOpenQuiz(!isOpenQuiz);
                      }}
                    >
                      <DropdownItem>Quiz</DropdownItem>
                    </li>
                  </DropdownMenu>
                </UncontrolledDropdown>
              </div>
            )}
            {(isOpenQuiz || isOpenSpinTheWheel) && (
              <Button
                className="btn btn-success btn-md mb-3 mt-2"
                color="primary"
                onClick={() => {
                  setIsOpenQuiz(false);
                  setIsOpenSpinTheWheel(false);
                  setCurrentSelectedGameDetails(null);
                  updateURLParams({ page: 1, perPageLimit: Constant.TABLE_PAGE_SIZE });
                }}
              >
                Manage Gamification
              </Button>
            )}
          </div>

          {!isOpenQuiz && !isOpenSpinTheWheel && (
            <>
              <div className="scrollable-1 mt-3 mb-3">
                <div className="d-inline-flex gap-4">
                  {cardData?.map((item, key) => (
                    <div className="card dashboard-globe-toyota-card" key={key}>
                      <CustomCard cardData={item} />
                    </div>
                  ))}
                </div>
              </div>
              <Table
                data={getGamificationList?.games}
                totalCount={getGamificationList?.pagination?.total_count}
                pageNumber={currentPageNumber}
                limit={perPageLimit}
                handleRowsPerPageChange={handleRowsPerPageChange}
                handlePageChange={paginationHandler}
                actionHandler={tableActionHandler}
              />
            </>
          )}

          {isOpenSpinTheWheel && <SpinTheWheel currentGameDetails={currentSelectedGameDetails} />}
          {isOpenQuiz && <Quiz currentGameDetails={currentSelectedGameDetails} />}
        </Container>
      </div>
    </>
  );
};

export default Gamification;
